import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useAppConfig } from '../contexts/AppConfigContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Copy, Link as LinkIcon, ShieldCheck, TrendingUp, Lock } from 'lucide-react';
import { toast } from 'sonner';

const SUNSET = '#FF5733';

/**
 * Tab 7 — Reward.
 *
 * • Sunset-orange 10-dot grid; dot flips orange only after the invitee logs
 *   in AFTER paying (backend anti-cheat gate).
 * • Dynamic status banner: default (0-4), tier1 (5-9), max (10).
 * • Anti-stacking discount ledger surfaced from /api/beta/reward/state.
 */
export default function RewardTab() {
    const { user, isPaid } = useAuth();
    const cfg = useAppConfig();
    const [state, setState] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get('/beta/reward/state');
                if (!cancelled) setState(data);
            } catch (_) {
                if (!cancelled) setState({});
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    if (loading || !user) {
        return (
            <div
                data-testid="reward-tab-loading"
                className="py-10 text-muted-foreground text-sm"
            >
                Loading rewards…
            </div>
        );
    }

    const code = state?.referral_code || user.referral_code || '';
    const inviteLink = `${window.location.origin}/login?ref=${code}`;
    const inviteTarget = state?.invite_target ?? 10;
    const activated = state?.activated_count ?? 0;
    const activatedRaw = state?.activated_raw_count ?? activated;
    const perRefGb = state?.per_referral_gb ?? 20;
    const bonusGb = state?.referral_bonus_gb ?? 0;
    const bonusCap = state?.referral_bonus_cap_gb ?? 400;
    const banner = state?.banner_variant ?? 'default';
    const m3pct = state?.queued_discount_month_3_pct ?? 0;
    const betaPhase = !!state?.beta_active;

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(inviteLink);
            toast.success('Invite link copied');
        } catch (_) {
            toast.error('Copy failed — long-press the link to copy manually.');
        }
    };

    /**
     * Banner text is phase-aware:
     * - Beta phase: strictly free-storage messaging. No 15% / 30% Month 3
     *   language surfaced — those recurring-discount ladders only activate
     *   once the retail phase goes live.
     * - Retail phase: 3-tier ladder (default / 5-9 / 10+) drives the copy.
     */
    const bannerText = (() => {
        if (betaPhase) {
            return '💼 Founding Member Status Verified: A flat 50% discount is locked for your Month 2 subscription renewal — automatically applied the moment you activate any paid retail tier. Every referral now compounds pure free storage instead of stacking future invoices.';
        }
        if (banner === 'max') {
            return '💼 Founding Member Status Verified: Month 2 is locked at 50% OFF! You have maximized the pipeline—a 30% discount is queued automatically for your Month 3 invoice! 🚀';
        }
        if (banner === 'tier1') {
            return '💼 Founding Member Status Verified: Month 2 is locked at 50% OFF! Your 5 active referrals have unlocked a 15% discount queued automatically for Month 3.';
        }
        return '💼 Founding Member Status Verified: A flat 50% discount is locked for your Month 2 subscription renewal! ⚡ Any referral discounts you accumulate this cycle will automatically queue and apply to your Month 3 billing statement.';
    })();

    return (
        <div className="space-y-8" data-testid="reward-tab-root">
            {/* Dynamic status banner */}
            <div
                data-testid="reward-status-banner"
                className="border-l-4 p-5 bg-card"
                style={{ borderLeftColor: SUNSET }}
            >
                <div className="flex items-start gap-3">
                    <ShieldCheck className="h-5 w-5 shrink-0 mt-0.5" style={{ color: SUNSET }} />
                    <p className="text-sm leading-relaxed">{bannerText}</p>
                </div>
            </div>

            {/* Invite link */}
            <section>
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Your invite link</p>
                <p className="mt-2 text-sm text-muted-foreground max-w-2xl leading-relaxed">
                    {betaPhase
                        ? `Each activated paying invitee earns you +${perRefGb} GB (cap ${bonusCap} GB total across ${inviteTarget} passes). Dots turn orange after they sign up, log in, and complete payment.`
                        : 'Refer friends. When they subscribe to any paid tier, your referral tier ladder decides your next-invoice discount (5-9 = 15% off, 10+ = 30% off).'}
                </p>

                {isPaid ? (
                <Card className="mt-5 p-6 bg-card border-border">
                    <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                        Share your link
                    </Label>
                    <div className="mt-3 flex flex-wrap gap-3 items-center">
                        <div className="flex-1 min-w-[240px] flex items-center gap-2 border border-border bg-background px-3 h-11 font-mono text-xs">
                            <LinkIcon className="h-4 w-4" style={{ color: SUNSET }} />
                            <span data-testid="invite-link-value" className="truncate">{inviteLink}</span>
                        </div>
                        <Button
                            data-testid="copy-invite-link-btn"
                            onClick={copy}
                            className="text-white h-11"
                            style={{ backgroundColor: SUNSET }}
                        >
                            <Copy className="h-4 w-4 mr-2" /> Copy Link
                        </Button>
                    </div>
                    <p className="mt-3 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                        Referral code: <span className="font-mono text-foreground">{code}</span>
                    </p>
                </Card>
                ) : (
                    <div
                        data-testid="referral-locked-banner"
                        className="mt-5 flex items-start gap-3 rounded-lg border p-6 bg-card"
                        style={{ borderColor: `${SUNSET}55`, backgroundColor: `${SUNSET}0A` }}
                    >
                        <Lock className="h-5 w-5 shrink-0 mt-0.5" style={{ color: SUNSET }} />
                        <p className="text-sm leading-relaxed">
                            🔒 Upgrade to a Paid Plan to activate your custom referral link and start earning rewards.
                        </p>
                    </div>
                )}
            </section>

            {/* Phase-aware activation grid: 20 dots (2×10) in beta, 10 dots in retail */}
            <section>
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <p
                        className="text-xs uppercase tracking-[0.24em]"
                        style={{ color: betaPhase ? SUNSET : undefined }}
                        data-testid="reward-grid-heading"
                    >
                        {betaPhase
                            ? `⚡ Beta Storage Pipeline (+${perRefGb} GB Per Conversion)`
                            : 'Retail Subscription Discount Ladder'}
                    </p>
                    <span className="text-xs text-muted-foreground">
                        Storage earned: <span className="font-mono text-foreground">+{bonusGb} / {bonusCap} GB Max</span>
                    </span>
                </div>
                <Card className="mt-3 p-6 sm:p-8 bg-card border-border">
                    {betaPhase ? (
                        <div className="space-y-4" data-testid="reward-dot-grid" data-grid-size="20">
                            {[0, 1].map((row) => (
                                <div
                                    key={row}
                                    className="flex items-center justify-center gap-3 flex-wrap"
                                >
                                    {Array.from({ length: 10 }).map((_, col) => {
                                        const i = row * 10 + col;
                                        const filled = i < activated;
                                        return (
                                            <div
                                                key={i}
                                                data-testid={`reward-dot-${i}`}
                                                data-filled={filled}
                                                className="h-6 w-6 rounded-full transition-all duration-300 border-2 hover:scale-125 cursor-default"
                                                style={{
                                                    backgroundColor: filled ? SUNSET : 'transparent',
                                                    borderColor: filled ? SUNSET : 'rgba(255,255,255,0.2)',
                                                    boxShadow: filled ? `0 0 12px ${SUNSET}66` : 'none',
                                                }}
                                                aria-label={filled ? 'Activated invite' : 'Awaiting activation'}
                                            />
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div
                            className="flex items-center justify-center gap-3 flex-wrap"
                            data-testid="reward-dot-grid"
                            data-grid-size="10"
                        >
                            {Array.from({ length: 10 }).map((_, i) => {
                                const filled = i < activated;
                                return (
                                    <div
                                        key={i}
                                        data-testid={`reward-dot-${i}`}
                                        data-filled={filled}
                                        className="h-6 w-6 rounded-full transition-all duration-300 border-2 hover:scale-125 cursor-default"
                                        style={{
                                            backgroundColor: filled ? SUNSET : 'transparent',
                                            borderColor: filled ? SUNSET : 'rgba(255,255,255,0.2)',
                                            boxShadow: filled ? `0 0 12px ${SUNSET}66` : 'none',
                                        }}
                                        aria-label={filled ? 'Activated invite' : 'Awaiting activation'}
                                    />
                                );
                            })}
                        </div>
                    )}

                    <p
                        data-testid="reward-invites-count"
                        className="mt-6 text-center font-heading text-lg"
                    >
                        {betaPhase ? (
                            <>
                                <span style={{ color: SUNSET }}>{activatedRaw}</span>
                                <span className="text-muted-foreground">
                                    {' '}/ {inviteTarget} Active Passes Verified
                                </span>
                                <span className="block mt-1.5 text-[13px] font-normal text-muted-foreground">
                                    (<span className="font-mono text-foreground">+{bonusGb} GB</span> /{' '}
                                    <span className="font-mono">+{bonusCap} GB Max</span>)
                                </span>
                            </>
                        ) : (
                            <>
                                <span style={{ color: SUNSET }}>{activated}</span>
                                <span className="text-muted-foreground">/{inviteTarget} Invites Activated</span>
                            </>
                        )}
                    </p>

                    {betaPhase && activatedRaw > inviteTarget && (
                        <p
                            data-testid="reward-overflow-note"
                            className="mt-3 text-center text-[11px]"
                            style={{ color: SUNSET }}
                        >
                            🎉 {activatedRaw - inviteTarget} bonus activation{activatedRaw - inviteTarget === 1 ? '' : 's'} beyond the +{bonusCap} GB storage cap — thank you for stretching the pipeline!
                        </p>
                    )}
                    <p className="mt-2 text-center text-[11px] text-muted-foreground">
                        Dots stay gray until the invitee signs up, logs in, and completes payment.
                    </p>
                </Card>
            </section>

            {/* Discount ledger (anti-stacking) — beta phase = Month 2 only.
                Month 3 referral-tier ladder appears only after retail phase goes live. */}
            <section>
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    Discount ledger (anti-stacking)
                </p>
                <div
                    className={`mt-3 grid grid-cols-1 gap-4 ${betaPhase ? '' : 'md:grid-cols-2'}`}
                >
                    <Card
                        data-testid="ledger-month-2"
                        className={`p-5 border-l-4 ${betaPhase ? 'md:max-w-md' : ''}`}
                        style={{ borderLeftColor: SUNSET }}
                    >
                        <div className="flex items-center gap-3">
                            <TrendingUp className="h-4 w-4" style={{ color: SUNSET }} />
                            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                Month 2 · founding perk
                            </p>
                        </div>
                        <p className="mt-3 font-heading text-3xl" style={{ color: SUNSET }}>
                            50% OFF
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">
                            Locked in — applies to your first retail renewal after beta.
                        </p>
                    </Card>
                    {!betaPhase && (
                        <Card
                            data-testid="ledger-month-3"
                            className={`p-5 border-l-4 ${m3pct > 0 ? '' : 'opacity-60'}`}
                            style={{ borderLeftColor: m3pct > 0 ? SUNSET : 'rgba(255,255,255,0.15)' }}
                        >
                            <div className="flex items-center gap-3">
                                <TrendingUp className="h-4 w-4" style={{ color: m3pct > 0 ? SUNSET : 'currentColor' }} />
                                <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                    Month 3 · referral tier
                                </p>
                            </div>
                            <p
                                className="mt-3 font-heading text-3xl"
                                style={{ color: m3pct > 0 ? SUNSET : undefined }}
                            >
                                {m3pct > 0 ? `${m3pct}% OFF` : '— %'}
                            </p>
                            <p className="mt-1 text-xs text-muted-foreground">
                                {m3pct > 0
                                    ? 'Queued — applies to your Month 3 invoice automatically.'
                                    : 'Reach 5 activations to unlock 15% · 10 for 30%.'}
                            </p>
                        </Card>
                    )}
                </div>
                <p className="mt-3 text-[11px] text-muted-foreground">
                    {betaPhase
                        ? 'Beta rewards are strictly free storage expansions. Recurring subscription discount ladders unlock once the live retail phase goes live.'
                        : 'Discounts never stack on a single invoice. The founding 50% applies to Month 2 exclusively; any secondary referral reward queues strictly for Month 3.'}
                </p>
            </section>
        </div>
    );
}
